/**
 * Turning a pile of browser `File` objects into something the analyzers can read.
 *
 * The analyzers are pure by design: they take an inventory and some decoded
 * text, and they never touch a disk, a network or a Tauri command. That is what
 * lets the same rules run over an uploaded folder in PackBench, a zip in Code
 * Smith, and a directory in the desktop app.
 *
 * Somebody still has to do the reading, though, and doing it four times in four
 * views is how four tools end up disagreeing about which files count as text.
 * So this is the one browser-side collector. The desktop app has its own, in
 * `desktop/src/lib/resourceCollect.ts`, because it reads over an IPC bridge
 * rather than from `File` objects — but both produce the same `GtaResourceInput`
 * and both stop at the same limits.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE LIMITS ARE HERE AND NOT IN THE ANALYZER
 *
 * A vehicle pack is routinely a gigabyte of `.yft` and `.ytd`. Reading all of
 * that to answer questions about `.meta` files would freeze the tab, and the
 * answer would not change. The caps below are I/O policy — how much this tool is
 * willing to read — and they belong next to the reading. The analyzer's job is
 * to be honest about what it was given, which is why it reports what it could
 * not check rather than assuming an unread file was fine.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { disambiguateLabels, EM_DASH_LABEL_STYLE } from "./labels";
import { extensionOf, normalizeName, normalizePath } from "./normalize";
import type { GtaFile, GtaResourceInput } from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/** Files read as text at all. Everything else is inventory only. */
export const TEXT_EXTENSIONS: ReadonlySet<string> = new Set([
  "lua",
  "meta",
  "xml",
  "json",
  "cfg",
  "txt",
  "js",
  "ts",
  "html",
  "css",
  "md",
  "sql",
]);

/** Cap one file's decoded text, so a 200 MB log cannot take the tab with it. */
const MAX_TEXT_BYTES = 4 * 1024 * 1024;

/** Cap the total decoded, for the same reason across a whole folder drop. */
const MAX_TOTAL_TEXT_BYTES = 32 * 1024 * 1024;

/** Cap the inventory. A drop larger than this is a mistake, not a resource. */
const MAX_FILES = 20_000;

// ─── Input shape ─────────────────────────────────────────────────────────────

/**
 * A file plus where it sits inside its resource.
 *
 * `path` is relative to the resource root, which the caller knows and this does
 * not: a folder drop, a zip and a desktop scan all root differently.
 */
export type CollectableFile = {
  path: string;
  file: File;
};

export type CollectResult = {
  input: GtaResourceInput;
  /** What was left unread, in words a user can act on. Empty when nothing was. */
  truncated: string[];
};

/**
 * One resource on its way to the analyzer, with the attribution intake has.
 *
 * `name` alone was the whole shape here, and that was the bug: `intake.ts`
 * produces a unique `root` and an `origins` map precisely so that two vendor
 * zips both shipping `police/` stay two resources, and a collector that accepts
 * only a name throws both away at the door. Everything downstream then sees one
 * resource called `police` and reports it clean — see `GtaResourceInput.identity`.
 *
 * `root` and `origin` are optional so the older call shape — `{ name, files }`,
 * which PackBench and the desktop scanner still pass — keeps working unchanged
 * and means exactly what it used to: one resource, identified by its name.
 */
export type CollectableGroup = {
  name: string;
  files: CollectableFile[];
  /**
   * Drop-relative directory this resource starts at, unique within a drop.
   * `DroppedResource.root`, passed straight through as the resource's identity.
   *
   * Unique *within one drop*, and not further. Every entry point in `intake.ts`
   * namespaces the roots it produces against each other, and none of them knows
   * about a previous call: a user who picks a folder called `police` twice, in
   * two separate gestures, gets the root `police` from both, and a caller that
   * unions the two lists is handing two different packs one identity. Nothing
   * downstream can then separate them — the stream dedupe key, the collision
   * count and the export labels all key on this — so `collectResources` says so
   * in words rather than letting the caller believe the two folded cleanly into
   * one. See `sharedIdentityNotices`.
   */
  root?: string;
  /** The archive or pick it came from: `ResourceDrop.origins.get(root)`. */
  origin?: string;
};

// ─── Collection ──────────────────────────────────────────────────────────────

/**
 * Read one resource's worth of files.
 *
 * Never rejects: a file that will not decode is skipped and named in
 * `truncated`, because a report that is missing one file and says so is more
 * use than an exception.
 *
 * `attribution` is how a caller that knows where the resource came from says so.
 * Omitting it is not a lesser result for a caller with one folder in hand — the
 * resource is then identified by its name, which is unique when there is only
 * one of it. It matters the moment there are two, and then leaving it out is
 * what merges them. See `CollectableGroup`.
 *
 * The sentences in `truncated` are deliberately *not* qualified by the resource
 * here. A caller that asked for one resource knows which one came back, and a
 * prefix on every notice would be noise in the single-pack case that is most of
 * this tool's traffic. The qualifier belongs to `collectResources`, which is the
 * call that can be holding two packs whose notices read identically.
 */
export async function collectResource(
  name: string,
  files: CollectableFile[],
  attribution?: { root?: string; origin?: string },
): Promise<CollectResult> {
  const truncated: string[] = [];
  const inventory: GtaFile[] = [];
  const texts = new Map<string, string>();

  const capped = files.slice(0, MAX_FILES);
  if (files.length > capped.length) {
    truncated.push(
      `Only the first ${MAX_FILES.toLocaleString()} of ${files.length.toLocaleString()} files were read.`,
    );
  }

  let totalText = 0;
  let skippedForSize = 0;

  for (const entry of capped) {
    const path = normalizePath(entry.path);
    const fileName = path.split("/").pop() ?? path;
    inventory.push({
      path,
      name: fileName,
      extension: extensionOf(fileName),
      size: entry.file.size,
    });

    if (!TEXT_EXTENSIONS.has(extensionOf(fileName))) continue;
    if (entry.file.size > MAX_TEXT_BYTES || totalText + entry.file.size > MAX_TOTAL_TEXT_BYTES) {
      skippedForSize += 1;
      continue;
    }

    try {
      const text = await entry.file.text();
      texts.set(path.toLowerCase(), stripBom(text));
      totalText += entry.file.size;
    } catch {
      truncated.push(`${path} could not be read as text.`);
    }
  }

  if (skippedForSize > 0) {
    truncated.push(
      `${skippedForSize} text file${skippedForSize === 1 ? " was" : "s were"} too large to read, ` +
        "so anything they declare was not checked.",
    );
  }

  // `identity` falls back to the name rather than being left undefined, so that
  // every consumer has one field to read instead of a fallback each; `origin`
  // does not, because there is no honest stand-in for "which zip this came from"
  // and a made-up one would be printed to a user as fact.
  const root = attribution?.root;
  const origin = attribution?.origin;
  return {
    input: {
      name,
      identity: root !== undefined && root !== "" ? root : name,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
      files: inventory,
      texts,
    },
    truncated,
  };
}

/**
 * Read several resources at once, for the checks that only exist between them.
 *
 * Sequential rather than parallel on purpose: the browser will happily start two
 * thousand concurrent reads and then spend longer scheduling them than reading.
 *
 * This is the call where attribution is not optional in practice. A caller with
 * several resources in hand is by definition in the case where two of them can
 * share a name, and a `ResourceDrop` already answers the question — `root` on
 * each resource, `origin` in the drop's `origins` map, keyed by that root. Pass
 * both; the cost of not passing them is a report that says two vendor packs are
 * one pack and finds nothing wrong with it.
 *
 * ── Why every notice is qualified before the Set sees it ─────────────────────
 *
 * The sentences `collectResource` writes name a resource-relative path or a bare
 * count, and two vendor zips with the same internal layout — the fixture this
 * whole layer is built on — produce them byte for byte identically. Deduplicating
 * on that text alone folded two packs' notices into one: an unreadable
 * `data/vehicles.meta` in *both* packs told the user exactly once that one file
 * had gone unread, named neither pack, and the vehicle rules had silently not run
 * twice. That is the same shape as the defect this thread was opened to close —
 * an input collapsed by a key two packs share, with nothing saying so — in the
 * one function that already has the attribution needed to keep them apart.
 *
 * The Set stays, so a notice a single resource genuinely repeats is still said
 * once.
 */
export async function collectResources(
  groups: CollectableGroup[],
): Promise<{ inputs: GtaResourceInput[]; truncated: string[] }> {
  const inputs: GtaResourceInput[] = [];
  const truncated: string[] = [];
  // Qualified only as far as it has to be, by the rule the report headings and
  // the collision sentence use, so an ordinary drop of three differently-named
  // resources does not grow a root on every line nobody needed to read.
  const labels = disambiguateLabels(
    groups.map((group) => ({ name: group.name, identity: group.root, origin: group.origin })),
    EM_DASH_LABEL_STYLE,
  );

  for (const [index, group] of groups.entries()) {
    const result = await collectResource(group.name, group.files, {
      root: group.root,
      origin: group.origin,
    });
    inputs.push(result.input);
    const label = labels[index] ?? "";
    for (const notice of result.truncated) {
      truncated.push(label === "" ? notice : `${label}: ${notice}`);
    }
  }

  truncated.push(...sharedIdentityNotices(inputs));
  return { inputs, truncated: [...new Set(truncated)] };
}

/**
 * A sentence for every identity that two resources in this one call share.
 *
 * `intake.ts` makes roots unique within a drop, and this function cannot check
 * that its groups came from one drop. A surface that lets a user add a second
 * folder and analyses the union is the case that breaks it: two picks of a
 * folder called `police` both yield the root `police` and the origin `police`,
 * so nothing in the data distinguishes them, `collectStreamAssets` treats the
 * second pack's `stream/police.yft` as a re-listing of the first's, and the
 * report comes back green — the original defect, reached from the other end.
 *
 * Reported rather than repaired, because there is no honest repair available
 * here. Synthesising `police (2)` would put a root in front of a reader that is
 * on nobody's disk, and inventing provenance is the one thing this layer will
 * not do. What it can do is refuse to let the collapse be silent. No product
 * surface unions drops today; this is the sentence the first one to try gets
 * instead of a clean bill of health.
 */
function sharedIdentityNotices(inputs: readonly GtaResourceInput[]): string[] {
  const counts = new Map<string, number>();
  for (const input of inputs) {
    const identity = normalizeName(input.identity ?? input.name);
    if (identity === "") continue;
    counts.set(identity, (counts.get(identity) ?? 0) + 1);
  }
  const notices: string[] = [];
  for (const [identity, count] of counts) {
    if (count < 2) continue;
    notices.push(
      `${count} resources in this scan were given the same identity "${identity}", so nothing ` +
        "downstream can tell them apart: their stream assets fold together and a collision " +
        "between them will not be reported. Namespace the picks before merging them.",
    );
  }
  return notices;
}

/**
 * A byte-order mark at the start of a `.meta` makes `DOMParser` fail on a file
 * that is otherwise perfectly valid, and Windows editors add one without asking.
 */
function stripBom(text: string): string {
  return text.replace(/^\uFEFF/, "");
}
